import { downloadFile, http, upload, type UploadOptions } from '@/lib/http';
import type { DateOnly, DateRangeParams, Decimal, PagedResult, RowVersion, UtcDateTime } from './types';

/**
 * Training (đào tạo) — courses, classes, sessions, students, enrollments, attendance, assessments/attempts, student import.
 * Mirrors apps/api/src/Crm.Api/Modules/Training/*.cs. Decimals are strings.
 */

export const TP = {
  classRead: 'training.class.read',
  classReadOwn: 'training.class.read.own',
  classManage: 'training.class.manage',
  studentManage: 'training.student.manage',
  studentPiiRead: 'training.student.pii.read',
  attendanceMark: 'training.attendance.mark',
  examManage: 'training.exam.manage',
  importRun: 'training.import.run',
} as const;

export const CLASS_READ = [TP.classRead, TP.classReadOwn, TP.classManage] as const;
export const STUDENT_READ = [TP.studentManage, TP.classRead, TP.classManage] as const;

// ---------- Courses ----------
export interface CompletionRule {
  minAttendancePercent: Decimal | null;
  passScore: Decimal | null;
  requireExam: boolean;
  countExcusedAsAttended: boolean;
}

export interface CourseVersionDto {
  id: number;
  courseId: number;
  versionNo: number;
  durationMinutes: number;
  completionRule: CompletionRule;
  contentRef: string | null;
  changeNote: string | null;
  createdAtUtc: UtcDateTime;
  isCurrent: boolean;
}

export interface CourseDto {
  id: number;
  code: string;
  name: string;
  description: string | null;
  trainingCategoryId: number | null;
  /** Internal ACTIVE / INACTIVE. */
  status: string;
  currentVersionId: number | null;
  currentVersion: CourseVersionDto | null;
  version: RowVersion;
}

export interface CreateCourseRequest {
  /** Blank/null → generated from series COURSE. */
  code: string | null;
  name: string;
  description: string | null;
  trainingCategoryId: number | null;
  durationMinutes: number;
  completionRule: CompletionRule | null;
  contentRef: string | null;
}

export interface UpdateCourseRequest {
  name: string;
  description: string | null;
  trainingCategoryId: number | null;
  status: string;
  version: RowVersion;
}

export interface NewCourseVersionRequest {
  durationMinutes: number;
  completionRule: CompletionRule;
  contentRef: string | null;
  changeNote: string;
}

export interface SubjectDto {
  id: number;
  code: string;
  name: string;
  courseId: number | null;
  version: RowVersion;
}

// ---------- Classes & sessions ----------
export interface ClassDto {
  id: number;
  code: string;
  name: string;
  projectId: number;
  courseVersionId: number;
  courseCode: string | null;
  courseName: string | null;
  courseVersionNo: number | null;
  capacity: number;
  holdingCount: number;
  status: string;
  statusName: string;
  instructorStaffId: number | null;
  instructorName: string | null;
  periodCode: string | null;
  sbdScopeKey: string;
  locationText: string | null;
  provinceCode: string | null;
  provinceName: string | null;
  plannedStart: DateOnly | null;
  plannedEnd: DateOnly | null;
  cancelReason: string | null;
  completedAtUtc: UtcDateTime | null;
  version: RowVersion;
}

export interface CreateClassRequest {
  /** Create only: blank/null → generated from series CLASS. */
  code?: string | null;
  projectId: number;
  courseVersionId: number;
  name: string;
  capacity: number;
  instructorStaffId: number | null;
  periodCode: string | null;
  locationText: string | null;
  provinceCode: string | null;
  provinceName: string | null;
  plannedStart: DateOnly | null;
  plannedEnd: DateOnly | null;
}

export type UpdateClassRequest = Omit<CreateClassRequest, 'projectId' | 'courseVersionId' | 'code'> & { version: RowVersion };

export interface SessionDto {
  id: number;
  classId: number;
  sessionNo: number;
  title: string | null;
  subjectId: number | null;
  startsAtUtc: UtcDateTime;
  endsAtUtc: UtcDateTime;
  startsAtLocal: string;
  endsAtLocal: string;
  timeZoneId: string;
  instructorStaffId: number | null;
  instructorName: string | null;
  deliveryMode: string;
  /** Internal PLANNED / DONE / CANCELLED. */
  sessionStatus: string;
  locationText: string | null;
  cancelReason: string | null;
  version: RowVersion;
}

export interface UpsertSessionRequest {
  startsAtUtc: UtcDateTime;
  endsAtUtc: UtcDateTime;
  timeZoneId: string | null;
  instructorStaffId: number | null;
  deliveryMode: string | null;
  title: string | null;
  subjectId: number | null;
  locationText: string | null;
  version: RowVersion | null;
}

export interface EnrollmentResultDto {
  enrollmentId: number;
  studentId: number;
  studentCode: string;
  fullName: string;
  status: string;
  passed: boolean;
  attendancePercent: Decimal;
  reasons: string[];
}

// ---------- Attendance ----------
export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'LATE' | 'EXCUSED';

export interface AttendanceDto {
  enrollmentId: number;
  studentId: number;
  studentCode: string;
  fullName: string;
  candidateNumber: string | null;
  status: AttendanceStatus | null;
  note: string | null;
  markedBy: number | null;
  markedAtUtc: UtcDateTime | null;
}

export interface AttendanceMarkInput {
  enrollmentId: number;
  status: AttendanceStatus;
  note: string | null;
}

export interface BulkAttendanceResult {
  created: number;
  updated: number;
  unchanged: number;
}

// ---------- Assessments ----------
export interface AssessmentDto {
  id: number;
  classId: number;
  title: string;
  maxScore: Decimal;
  passScore: Decimal;
  method: string;
  versionNo: number;
  isRequired: boolean;
  locked: boolean;
  version: RowVersion;
}

export interface CreateAssessmentRequest {
  title: string;
  maxScore: Decimal;
  passScore: Decimal | null;
  method: string | null;
  isRequired: boolean;
}

export interface AttemptDto {
  id: number;
  assessmentId: number;
  enrollmentId: number;
  attemptNo: number;
  score: Decimal | null;
  /** PASS / FAIL / ABSENT */
  resultStatus: string;
  submittedAtUtc: UtcDateTime;
  correctsAttemptId: number | null;
  reason: string | null;
  recordedBy: number;
  createdAtUtc: UtcDateTime;
  isLatest: boolean;
}

export interface RecordAttemptRequest {
  enrollmentId: number;
  score: Decimal | null;
  absent: boolean;
  submittedAtUtc: UtcDateTime | null;
  correctsAttemptId: number | null;
  reason: string | null;
}

// ---------- Students & enrollments ----------
export interface StudentDto {
  id: number;
  studentCode: string;
  fullName: string;
  dateOfBirth: DateOnly | null;
  gender: string | null;
  /** Masked ("********1234") unless the caller holds training.student.pii.read. */
  idNumber: string | null;
  idNumberMasked: boolean;
  phone: string | null;
  email: string | null;
  externalRef: string | null;
  employerCustomerId: number | null;
  employerName: string | null;
  version: RowVersion;
  createdAtUtc: UtcDateTime;
}

export interface StudentEnrollmentDto {
  enrollmentId: number;
  classId: number;
  classCode: string;
  className: string;
  periodCode: string | null;
  candidateNumber: string | null;
  status: string;
  joinedAtUtc: UtcDateTime;
  exitedAtUtc: UtcDateTime | null;
  completionResult: string | null;
  transferFromId: number | null;
}

export interface StudentDetail {
  student: StudentDto;
  enrollments: StudentEnrollmentDto[];
}

export interface UpsertStudentRequest {
  /** Create only (ignored on update): blank/null → generated from series STUDENT. */
  code?: string | null;
  fullName: string;
  dateOfBirth: DateOnly | null;
  gender: string | null;
  idNumber: string | null;
  phone: string | null;
  email: string | null;
  externalRef: string | null;
  employerCustomerId: number | null;
  employerName: string | null;
  version: RowVersion | null;
}

export interface EnrollmentDto {
  id: number;
  classId: number;
  studentId: number;
  studentCode: string;
  fullName: string;
  dateOfBirth: DateOnly | null;
  gender: string | null;
  idNumber: string | null;
  phone: string | null;
  email: string | null;
  employerName: string | null;
  candidateNumber: string | null;
  status: string;
  statusName: string;
  joinedAtUtc: UtcDateTime;
  exitedAtUtc: UtcDateTime | null;
  withdrawnOn: DateOnly | null;
  exitReason: string | null;
  transferFromId: number | null;
  completionResult: string | null;
  attendancePercent: Decimal | null;
  version: RowVersion;
}

// ---------- Import ----------
export interface ImportJobDto {
  id: number;
  importType: string;
  fileName: string;
  documentId: number;
  classId: number | null;
  classCode: string | null;
  employerCustomerId: number | null;
  status: string;
  statusName: string;
  totalCount: number;
  processedCount: number;
  validCount: number;
  successCount: number;
  errorCount: number;
  matchedExistingCount: number;
  progressPercent: number;
  warningMessage: string | null;
  errorMessage: string | null;
  validatedAtUtc: UtcDateTime | null;
  commitRequestedAtUtc: UtcDateTime | null;
  completedAtUtc: UtcDateTime | null;
  createdBy: number | null;
  createdAtUtc: UtcDateTime;
  version: RowVersion;
}

/** Row statuses: VALID / INVALID / COMMITTED / FAILED. */
export interface ImportRowDto {
  id: number;
  rowNo: number;
  status: string;
  data: Record<string, string | null>;
  errors: string[];
  warnings: string[];
  matchedStudentId: number | null;
  resolvedEntityId: number | null;
  resolvedEnrollmentId: number | null;
}

// ---------- Lookups owned by Delivery (minimal read for pickers) ----------
export interface ProjectPickItem {
  id: number;
  code: string;
  name: string;
  status: string;
  orderCode: string | null;
}

export interface StaffPickItem {
  id: number;
  staffCode: string;
  name: string;
  isInstructor: boolean;
  status: string;
}

const t = '/training';

export const trainingApi = {
  // courses
  courses: (q?: { q?: string; status?: string }) => http.get<CourseDto[]>(`${t}/courses`, { ...q }),
  course: (id: number) => http.get<CourseDto>(`${t}/courses/${id}`),
  createCourse: (req: CreateCourseRequest) => http.post<CourseDto>(`${t}/courses`, req),
  updateCourse: (id: number, req: UpdateCourseRequest) => http.put<CourseDto>(`${t}/courses/${id}`, req),
  courseVersions: (id: number) => http.get<CourseVersionDto[]>(`${t}/courses/${id}/versions`),
  newCourseVersion: (id: number, req: NewCourseVersionRequest) => http.post<CourseVersionDto>(`${t}/courses/${id}/versions`, req),
  subjects: (courseId?: number) => http.get<SubjectDto[]>(`${t}/subjects`, { courseId }),
  saveSubject: (id: number | null, req: { code: string; name: string; courseId: number | null; version: RowVersion | null }) =>
    id ? http.put<SubjectDto>(`${t}/subjects/${id}`, req) : http.post<SubjectDto>(`${t}/subjects`, req),

  // classes
  classes: (q: { projectId?: number; status?: string; q?: string; page?: number; pageSize?: number } & DateRangeParams, signal?: AbortSignal) =>
    http.get<PagedResult<ClassDto>>(`${t}/classes`, { ...q }, { signal }),
  class: (id: number) => http.get<ClassDto>(`${t}/classes/${id}`),
  createClass: (req: CreateClassRequest) => http.post<ClassDto>(`${t}/classes`, req),
  updateClass: (id: number, req: UpdateClassRequest) => http.put<ClassDto>(`${t}/classes/${id}`, req),
  transitionClass: (id: number, req: { toStatus: string; reason: string | null; version: RowVersion }) =>
    http.post<ClassDto>(`${t}/classes/${id}/transitions`, req),
  results: (id: number) => http.get<EnrollmentResultDto[]>(`${t}/classes/${id}/results`),

  // sessions
  sessions: (classId: number) => http.get<SessionDto[]>(`${t}/classes/${classId}/sessions`),
  saveSession: (classId: number, sessionId: number | null, req: UpsertSessionRequest) =>
    sessionId
      ? http.put<SessionDto>(`${t}/classes/${classId}/sessions/${sessionId}`, req)
      : http.post<SessionDto>(`${t}/classes/${classId}/sessions`, req),
  cancelSession: (sessionId: number, reason: string) => http.post<void>(`${t}/sessions/${sessionId}/cancellations`, { reason }),

  // attendance
  attendance: (sessionId: number) => http.get<AttendanceDto[]>(`${t}/sessions/${sessionId}/attendance`),
  markAttendance: (sessionId: number, items: AttendanceMarkInput[]) =>
    http.put<BulkAttendanceResult>(`${t}/sessions/${sessionId}/attendance`, { items }),

  // assessments
  assessments: (classId: number) => http.get<AssessmentDto[]>(`${t}/classes/${classId}/assessments`),
  createAssessment: (classId: number, req: CreateAssessmentRequest) => http.post<AssessmentDto>(`${t}/classes/${classId}/assessments`, req),
  attempts: (assessmentId: number, enrollmentId?: number) =>
    http.get<AttemptDto[]>(`${t}/assessments/${assessmentId}/attempts`, { enrollmentId }),
  recordAttempt: (assessmentId: number, req: RecordAttemptRequest, key: string) =>
    http.post<AttemptDto>(`${t}/assessments/${assessmentId}/attempts`, req, { idempotencyKey: key }),

  // students
  students: (q: { q?: string; employerCustomerId?: number; page?: number; pageSize?: number } & DateRangeParams, signal?: AbortSignal) =>
    http.get<PagedResult<StudentDto>>(`${t}/students`, { ...q }, { signal }),
  student: (id: number) => http.get<StudentDetail>(`${t}/students/${id}`),
  saveStudent: (id: number | null, req: UpsertStudentRequest) =>
    id ? http.put<StudentDto>(`${t}/students/${id}`, req) : http.post<StudentDto>(`${t}/students`, req),

  // enrollments
  enrollments: (classId: number, q: { status?: string; q?: string; page?: number; pageSize?: number } = {}) =>
    http.get<PagedResult<EnrollmentDto>>(`${t}/classes/${classId}/enrollments`, { ...q }),
  enroll: (classId: number, req: { studentId: number; candidateNumber: string | null }) =>
    http.post<EnrollmentDto>(`${t}/classes/${classId}/enrollments`, req),
  transfer: (id: number, req: { targetClassId: number; candidateNumber: string | null; keepCandidateNumber: boolean; reason: string }, key: string) =>
    http.post<EnrollmentDto>(`${t}/enrollments/${id}/transfers`, req, { idempotencyKey: key }),
  withdraw: (id: number, req: { withdrawnOn: DateOnly; reason: string }) => http.post<EnrollmentDto>(`${t}/enrollments/${id}/withdrawals`, req),
  transitionEnrollment: (id: number, req: { toStatus: string; reason: string | null; version: RowVersion }) =>
    http.post<EnrollmentDto>(`${t}/enrollments/${id}/transitions`, req),
  setCandidateNumber: (id: number, req: { candidateNumber: string | null; version: RowVersion }) =>
    http.put<EnrollmentDto>(`${t}/enrollments/${id}/candidate-number`, req),

  // import
  downloadTemplate: () => downloadFile(`${t}/import-templates/students`, 'mau-import-hoc-vien.xlsx'),
  uploadImport: (file: File, meta: { classId?: number | null; employerCustomerId?: number | null }, opts?: UploadOptions) => {
    const form = new FormData();
    form.append('file', file);
    if (meta.classId != null) form.append('classId', String(meta.classId));
    if (meta.employerCustomerId != null) form.append('employerCustomerId', String(meta.employerCustomerId));
    return upload<ImportJobDto>(`${t}/import-jobs`, form, opts);
  },
  importJobs: (q: { classId?: number; page?: number; pageSize?: number } = {}) =>
    http.get<PagedResult<ImportJobDto>>(`${t}/import-jobs`, { ...q }),
  importJob: (id: number) => http.get<ImportJobDto>(`${t}/import-jobs/${id}`),
  importRows: (id: number, q: { status?: string; page?: number; pageSize?: number }) =>
    http.get<PagedResult<ImportRowDto>>(`${t}/import-jobs/${id}/rows`, { ...q }),
  downloadImportErrors: (id: number) => downloadFile(`${t}/import-jobs/${id}/errors`, `loi-import-${id}.xlsx`),
  /** Server-side idempotent; the key is sent anyway so a retried click maps to the same intent. */
  commitImport: (id: number, key: string) => http.post<ImportJobDto>(`${t}/import-jobs/${id}/commits`, {}, { idempotencyKey: key }),
  retryImport: (id: number) => http.post<ImportJobDto>(`${t}/import-jobs/${id}/retries`, {}),

  // delivery lookups (pickers)
  projects: (q: string, signal?: AbortSignal) =>
    http.get<PagedResult<ProjectPickItem>>('/delivery/projects', { q: q || undefined, pageSize: 20 }, { signal }),
  instructors: (q: string, signal?: AbortSignal) =>
    http.get<StaffPickItem[]>('/delivery/staff', { q: q || undefined, instructor: true, status: 'ACTIVE' }, { signal }),
};

/** Query keys — first segment = domain. */
export const tk = {
  all: ['training'] as const,
  courses: ['training', 'courses'] as const,
  courseVersions: (id: number) => ['training', 'courses', id, 'versions'] as const,
  subjects: (courseId?: number) => ['training', 'subjects', courseId ?? 'all'] as const,
  classes: ['training', 'classes'] as const,
  classList: (p: object) => ['training', 'classes', 'list', p] as const,
  class: (id: number) => ['training', 'classes', 'detail', id] as const,
  sessions: (classId: number) => ['training', 'classes', 'detail', classId, 'sessions'] as const,
  enrollments: (classId: number, p: object = {}) => ['training', 'classes', 'detail', classId, 'enrollments', p] as const,
  assessments: (classId: number) => ['training', 'classes', 'detail', classId, 'assessments'] as const,
  attempts: (assessmentId: number) => ['training', 'assessments', assessmentId, 'attempts'] as const,
  results: (classId: number) => ['training', 'classes', 'detail', classId, 'results'] as const,
  attendance: (sessionId: number) => ['training', 'sessions', sessionId, 'attendance'] as const,
  students: ['training', 'students'] as const,
  studentList: (p: object) => ['training', 'students', 'list', p] as const,
  student: (id: number) => ['training', 'students', 'detail', id] as const,
  importJobs: ['training', 'import-jobs'] as const,
  importJob: (id: number) => ['training', 'import-jobs', id] as const,
  importRows: (id: number, p: object) => ['training', 'import-jobs', id, 'rows', p] as const,
};
